import { Injectable, inject } from '@angular/core';
import type {
  LimitsValidationResponse,
  LimitsViolation,
} from '@b2b/common/services/salesRules/limits-validation.types';
import type { OrderUpdateInput } from '@b2b/common/services/api/ordersApi/types/api/calculate.type';

import { Q_PORT, type IQPort } from '../utils/q.port';
import { OrdersApiService } from '../api/orders/orders-api.service';

export interface LimitsValidationCallbacks {
  onSuccess: (messages: Array<{ message: string }>) => void;
  onError: (messages: Array<{ message: string }>) => void;
  hideMessages: () => void;
}

/**
 * OrderEditSalesRulesService (Angular 18)
 *
 * Replaces: apps/b2b-ui/common/services/salesRules/order-edit-sales-rules.service.ts
 * Validates ticket limits for order edit operations and tracks violations/overrides.
 */
@Injectable({ providedIn: 'root' })
export class OrderEditSalesRulesService {
  private $q = inject<IQPort>(Q_PORT);
  private ordersApi = inject(OrdersApiService);

  /** Limits violations per event, keyed by eventId */
  private violations: Record<string, LimitsViolation[]> = {};

  /** Whether limits override is active, keyed by eventId */
  private overrides: Record<string, boolean> = {};

  /** Whether limits have been checked, keyed by eventId */
  private checkedEvents: Record<string, boolean> = {};

  /**
   * Validates limits for an order edit operation
   */
  validateLimits(orderData: OrderUpdateInput, callbacks: LimitsValidationCallbacks): Promise<LimitsValidationResponse> {
    const deferred = this.$q.defer<LimitsValidationResponse>();

    callbacks.hideMessages();

    this.ordersApi
      .validateEntitlements(orderData)
      .then((response) => {
        const validationResponse = response.data.payload as LimitsValidationResponse;
        this.processValidationResponse(validationResponse, callbacks);
        deferred.resolve(validationResponse);
      })
      .catch((error) => {
        callbacks.onError([{ message: error.data?.error || 'Failed to validate limits' }]);
        deferred.reject(error);
      });

    return deferred.promise;
  }

  /**
   * Processes the validation response and triggers appropriate callbacks
   */
  processValidationResponse(response: LimitsValidationResponse, callbacks: LimitsValidationCallbacks): void {
    if (!response.results || response.results.length === 0) {
      callbacks.onSuccess([{ message: 'All ticket limits verified successfully' }]);
      return;
    }

    if (!response.valid) {
      if (response.messages && response.messages.length > 0) {
        callbacks.onError([{ message: 'Selected tickets are not within limits' }]);
      }

      if (response.violations && response.violations.length > 0) {
        response.violations.forEach((violation) => {
          const remaining = violation.allowedQuantity - violation.purchasedQuantity;
          console.warn(
            `Event ${violation.eventId}: Requested ${violation.requestedQuantity}, ` +
              `but only ${remaining} remaining (${violation.purchasedQuantity} already purchased)`,
          );
        });
      }

      return;
    }

    if (response.valid && response.passed && response.passed.length > 0) {
      callbacks.onSuccess([{ message: 'Member ticket limit: Selected tickets are within limits' }]);

      response.passed.forEach((passed) => {
        const remaining = passed.allowedQuantity - passed.purchasedQuantity;
        console.log(
          `Event ${passed.eventId}: ${remaining} tickets remaining ` +
            `(${passed.purchasedQuantity} purchased, ${passed.allowedQuantity} allowed)`,
        );
      });
    } else {
      callbacks.onSuccess([{ message: 'Member ticket limit: Selected tickets are within limits' }]);
    }
  }

  /**
   * Stores violations from a validation response, grouped by eventId.
   * Also marks all events in the response as checked.
   */
  storeViolations(response: LimitsValidationResponse): void {
    this.violations = {};

    if (response.violations && response.violations.length > 0) {
      for (const violation of response.violations) {
        if (!this.violations[violation.eventId]) {
          this.violations[violation.eventId] = [];
        }
        this.violations[violation.eventId].push(violation);
        this.checkedEvents[violation.eventId] = true;
      }
    }

    if (response.passed && response.passed.length > 0) {
      for (const passed of response.passed) {
        this.checkedEvents[passed.eventId] = true;
      }
    }
  }

  clearViolations(): void {
    this.violations = {};
  }

  getViolationsForEvent(eventId: string): LimitsViolation[] | undefined {
    return this.violations[eventId];
  }

  hasViolationsForEvent(eventId: string): boolean {
    const violations = this.violations[eventId];
    return Array.isArray(violations) && violations.length > 0;
  }

  toggleOverride(eventId: string): void {
    this.overrides[eventId] = !this.overrides[eventId];
  }

  isOverrideActive(eventId: string): boolean {
    return !!this.overrides[eventId];
  }

  clearOverrides(): void {
    this.overrides = {};
  }

  isLimitsChecked(eventId: string): boolean {
    return !!this.checkedEvents[eventId];
  }

  clearCheckedEvents(): void {
    this.checkedEvents = {};
  }

  /**
   * Returns true if there are no violations OR all events with violations have overrides active
   */
  allViolationsHaveOverrides(): boolean {
    const eventIdsWithViolations = Object.keys(this.violations);

    if (eventIdsWithViolations.length === 0) {
      return true;
    }

    return eventIdsWithViolations.every((eventId) => this.isOverrideActive(eventId));
  }
}
